// @workflow assistant.ui-tools
/**
 * The words and limits the 3D viewer tools share: argument limits, the wait budget, the reasons a
 * tag is skipped, the progress lines the user reads, and the plain-words details the model reads.
 * Defined once here; viewerArgs, viewerColors, viewerPrepare, viewerOutcome and viewerTools import them.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const VIEWER_LIMITS = Object.freeze({
  MAX_TAG_IDS: 500,
  MAX_COLOR_GROUPS: 8,
  MAX_TAG_ID_LENGTH: 64,
  MAX_FACILITY_ID_LENGTH: 64,
  /** What the colouring legend shows for a group, in characters. */
  MAX_LABEL_LENGTH: 40,
  /** How far a selected element is walked up the model tree to find the tag that owns it. */
  MAX_PARENT_DEPTH: 64,
});

/**
 * How long a tool waits for the facility and its 3D model to open, and how often it looks.
 * A large model can take longer; then the tool says so and the model may try again.
 */
export const VIEWER_WAIT = Object.freeze({ TIMEOUT_MS: 60 * 1000, POLL_MS: 250 });

export const FOCUS_MODE = Object.freeze({ ZOOM: 'zoom', ISOLATE: 'isolate' });

/**
 * Where focus_tags puts the 3D view when none is on screen (`target`): NEW opens a view beside the
 * user's (the default), CURRENT switches one open view to 3D (targetPaneSlot; "show it here").
 */
export const PANE_TARGET = Object.freeze({ NEW: 'new', CURRENT: 'current' });

/** How ghost mode shows what isn't isolated or highlighted (Quick Settings' wireframe / solid switch). */
export const GHOSTING_STYLE = Object.freeze({ WIREFRAME: 'wireframe', SOLID: 'solid' });

/** Why a tag that was asked for is not on screen (`data.skipped[].reason`). */
export const SKIP_REASON = Object.freeze({
  /** Not a tag of this facility (or not one the user can see). */
  NOT_FOUND: 'not_found',
  /** A tag of this facility, but with no geometry or point in the loaded 3D model. */
  NO_PLACE: 'no_place',
});

/**
 * Facts a viewer answer may carry beside `{ shown, skipped }`, for the server to word (AVA-744).
 * UNLINKED_FROM_360: the 3D view was linked to the open 360, so AVA unlinked it before isolating;
 * otherwise the link puts the camera back at the 360 and the isolated tags are not in view.
 */
export const VIEWER_FACT = Object.freeze({ UNLINKED_FROM_360: 'unlinkedFrom360' });

/** Who owns the scene the assistant paints, so another widget's clear can't blank it by mistake. */
export const ASSISTANT_FOCUS_OWNER = Object.freeze({ widgetId: 'ava-assistant', title: 'AVA Assistant' });

/** The colouring legend's heading for the assistant's colours. */
export const COLOR_LEGEND_TITLE = ASSISTANT_FOCUS_OWNER.title;

const UNNAMED_FACILITY = 'the facility';

/** What the user reads under the step while a tool prepares the viewer. */
export const VIEWER_PROGRESS = Object.freeze({
  OPENING_MODEL: 'Opening the 3D model…',
  openingFacility: (name) => `Opening ${name || UNNAMED_FACILITY}…`,
});

/** Plain words for the model: what went wrong and, where it can, what to do next. */
export const VIEWER_DETAIL = Object.freeze({
  NO_VIEWER: 'The 3D viewer is not available here.',
  NO_FACILITY: 'No facility is open. Try again with the facility to open (facilityId).',
  NO_ACCESS: 'This facility was not found, or the user cannot open it.',
  NOT_OPENED: 'AVA could not open this facility.',
  LIST_NOT_OPENED: 'AVA could not leave this page to open the other facility.',
  LEFT_PAGE: 'The user left the facility before the 3D model opened.',
  NO_MODEL: 'This facility has no 3D model.',
  NO_ROOM: 'All the views on the page are in use. Ask the user to close one, then try again.',
  CLOSED: 'The 3D view was closed before AVA could use it.',
  TIMED_OUT: 'The 3D model took too long to open. Try again.',
  PAGE_LOAD_FAILED: 'The 3D model could not be loaded on this page. Try again.',
  STOPPED: 'The request was stopped.',
  LOADING: 'The 3D model is still loading. Try again in a moment.',
  OTHER_FACILITY: 'This tag is in another facility. Try again with that facility (facilityId).',
  NOT_FOUND: 'None of these tags were found in this facility.',
  NO_PLACE: 'None of these tags have a place in the 3D model.',
  TAG_NOT_FOUND: 'This tag was not found in this facility.',
  TAG_NO_PLACE: 'This tag has no place in the 3D model.',
  REPLACED: 'Another view replaced this one before it finished.',
  NOT_SHOWN: 'The 3D viewer could not show these tags.',
  ZOOM_FAILED: 'The 3D viewer could not zoom to this tag.',
  NO_360: 'No 360 is open beside a 3D view, so there is nothing to link the 3D view to.',
  LINK_FAILED: 'AVA could not link the 3D view to the 360.',
});

/** Facility and tag ids compare without case (GUIDs arrive in either case). */
export const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined
  && String(a).toLowerCase() === String(b).toLowerCase();
